import { Status as ItemStatus } from './claim-item/status';
import { Category } from './claim-item/line/category';

/** Bump this when the maths changes, so old totals can still be explained. */
export const TOTALS_CALCULATION_VERSION = 1;

/** Currencies a charge can use. */
export const SUPPORTED_CURRENCIES = ['USD', 'CAD'] as const;

/** Rounds to the nearest cent; halves go up. */
export function roundTaxHalfUp(value: number): number {
  return Math.floor(value + 0.5);
}

/** Tax on one charge (0.065 = 6.5%). */
export function calculateChargeTax(base: number, rate: number): number {
  return roundTaxHalfUp(base * rate);
}

/** A claim gets one deductible, even when its jobs span coverages. */
export function resolveClaimDeductible(deductibles: number[], strategy: 'max' | 'min' = 'max'): number {
  const known = deductibles.filter(value => value !== null && value !== undefined && value >= 0);
  if (!known.length) return 0;
  return strategy === 'max' ? Math.max(...known) : Math.min(...known);
}

/** What the maths needs from each charge. */
export interface ChargeInput {
  id: string;
  category: Category;
  /** Kept for the record; we never convert currencies. */
  currency: string;
  requestedAmount: number;
  authorizedAmount: number | null;
  taxable: boolean;
  taxInclusive: boolean;
  calculatedTax: number | null;
  deleted: boolean;
}

export interface JobInput {
  id: string;
  status: ItemStatus;
  deleted: boolean;
  charges: ChargeInput[];
}

export interface JobTotals {
  jobId: string;
  status: ItemStatus;
  requestedTotal: number;
  authorizedGross: number;
  tax: number;
}

export interface ClaimTotals {
  calculationVersion: number;
  /** Every figure below is in cents of this currency. */
  currency: string;
  requestedTotal: number;
  authorizedGross: number;
  tax: number;
  deductible: number;
  netPayable: number;
  jobs: JobTotals[];
  /** The inputs used, so the result can be checked later. */
  source: {
    deductible: number;
    jobs: { id: string; status: ItemStatus; deleted: boolean; charges: ChargeInput[] }[];
  };
}

/** Only approved or resolved jobs count towards what we pay. */
function contributesToAuthorized(status: ItemStatus): boolean {
  return status === ItemStatus.APPROVED || status === ItemStatus.RESOLVED;
}

function calculateJobTotals(job: JobInput): JobTotals {
  const active = job.deleted ? [] : job.charges.filter(charge => !charge.deleted);

  const requestedTotal = active.reduce((sum, charge) => sum + charge.requestedAmount, 0);

  let authorizedGross = 0;
  let tax = 0;

  if (contributesToAuthorized(job.status)) {
    for (const charge of active) {
      const authorized = charge.authorizedAmount ?? 0;
      // Undecided charges add nothing, tax included.
      const chargeTax = charge.taxable && charge.authorizedAmount !== null ? (charge.calculatedTax ?? 0) : 0;

      tax += chargeTax;
      // Inclusive amounts already hold their tax; add it for the rest.
      authorizedGross += authorized + (charge.taxInclusive ? 0 : chargeTax);
    }
  }

  return { jobId: job.id, status: job.status, requestedTotal, authorizedGross, tax };
}

/** Asked for, approved plus tax, minus one deductible — never below zero. */
export function calculateClaimTotals(jobs: JobInput[], deductible: number, currency = 'USD'): ClaimTotals {
  const jobTotals = jobs.map(calculateJobTotals);

  const requestedTotal = jobTotals.reduce((sum, job) => sum + job.requestedTotal, 0);
  const authorizedGross = jobTotals.reduce((sum, job) => sum + job.authorizedGross, 0);
  const tax = jobTotals.reduce((sum, job) => sum + job.tax, 0);

  return {
    calculationVersion: TOTALS_CALCULATION_VERSION,
    currency,
    requestedTotal,
    authorizedGross,
    tax,
    deductible,
    netPayable: Math.max(0, authorizedGross - deductible),
    jobs: jobTotals,
    source: {
      deductible,
      jobs: jobs.map(job => ({ id: job.id, status: job.status, deleted: job.deleted, charges: job.charges })),
    },
  };
}
